import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const DEFAULT_MAP_CENTER = [-25.2744, 133.7751];
const DEFAULT_MAP_ZOOM = 4;
const MAP_FIT_PADDING = [80, 80];
const SINGLE_ASSET_ZOOM = 16;
const MAX_FIT_ZOOM = 16;
const STALE_LOCATION_MS = 30 * 24 * 60 * 60 * 1000;

const STATUS_RULES = {
  6: { label: 'Operational', color: '#23BB76' },
  10: { label: 'Maintenance', color: '#ffc107' },
  7: { label: 'Out of Service', color: '#dc3545' },
  unknown: { label: 'Unknown', color: '#6c757d' },
  stale: { label: 'Location older than 30 days', color: '#6c757d' },
};

const isFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed);
};

const isValidAssetCoordinate = (value) => isFiniteNumber(value) && Number(value) !== 0;

const formatDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
};

const isStaleLocation = (timestamp) => {
  if (!timestamp) return false;
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return false;
  return Date.now() - parsed.getTime() > STALE_LOCATION_MS;
};

const buildMarkerIcon = (color, text) =>
  L.divIcon({
    className: '',
    html: `
      <div style="
        display:flex;
        align-items:center;
        gap:6px;
        white-space:nowrap;
      ">
        <svg
          width="32"
          height="42"
          viewBox="0 0 24 36"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z"
            fill="${color}"
            stroke="#ffffff"
            stroke-width="2"
          />
          <circle
            cx="12"
            cy="12"
            r="4"
            fill="#ffffff"
          />
        </svg>

        <span style="
          font-size:12px;
          font-weight:600;
          color:#EC612C;
          text-shadow:
            0 0 2px #fff,
            0 0 4px #fff;
        ">
          ${text}
        </span>
      </div>
    `,
    iconSize: [120, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -42],
  });

const getMarkerStatusMeta = (marker) => {
  if (isStaleLocation(marker?.locationTimestamp)) return STATUS_RULES.stale;
  return STATUS_RULES[Number(marker?.plantStatusId)] || STATUS_RULES.unknown;
};

const fitMapToAssetBounds = (map, assets) => {
  if (!map || !Array.isArray(assets)) return false;
  const validAssets = assets.map((asset) => ({ lat: Number(asset.latitude), lng: Number(asset.longitude) }));
  if (!validAssets.length) return false;
  if (validAssets.length === 1) {
    map.setView([validAssets[0].lat, validAssets[0].lng], SINGLE_ASSET_ZOOM);
    return true;
  }
  const bounds = L.latLngBounds(validAssets.map(({ lat, lng }) => [lat, lng]));
  map.fitBounds(bounds, { padding: MAP_FIT_PADDING, maxZoom: MAX_FIT_ZOOM });
  return true;
};

const AssetMapView = ({ assetLocationMarkers, totalAssetsCount = 0, googleTileUrl, loading = false, error = '', onOpenAsset }) => {
  const [legendOpen, setLegendOpen] = useState(false);
  const mapRef = useRef(null);
  const hasFitBoundsRef = useRef(false);
  const lastFitSignatureRef = useRef('');

  const { validMarkers, invalidCoordinateCount } = useMemo(() => {
    const parsed = Array.isArray(assetLocationMarkers) ? assetLocationMarkers : [];
    let invalidCount = 0;
    const valid = parsed
      .map((location) => {
        const latitude = Number(location?.latitude);
        const longitude = Number(location?.longitude);
        if (!isValidAssetCoordinate(latitude) || !isValidAssetCoordinate(longitude)) {
          invalidCount += 1;
          return null;
        }
        return { ...location, coordinates: [latitude, longitude], statusMeta: getMarkerStatusMeta(location) };
      })
      .filter(Boolean);
    return { validMarkers: valid, invalidCoordinateCount: invalidCount };
  }, [assetLocationMarkers]);

  const summary = useMemo(() => {
    const maintenanceCount = validMarkers.filter((m) => [7, 10].includes(Number(m.plantStatusId))).length;
    const withWorkCount = validMarkers.filter((m) => Number(m.openWorkOrderCount || 0) > 0 || Number(m.openDefectCount || m.openWorkRequestCount || 0) > 0).length;
    const filteredAssetCount = Number(totalAssetsCount) > 0 ? Number(totalAssetsCount) : 0;
    const hasUsableMarkerDataset = !loading && !error && Array.isArray(assetLocationMarkers) && assetLocationMarkers.length > 0;
    const noLocationCount = hasUsableMarkerDataset
      ? Math.max(filteredAssetCount - validMarkers.length, 0)
      : invalidCoordinateCount;
    return { total: validMarkers.length, noLocation: noLocationCount, maintenanceCount, withWorkCount };
  }, [validMarkers, invalidCoordinateCount, totalAssetsCount, loading, error, assetLocationMarkers]);

  const mapAssetFitSignature = useMemo(() => validMarkers.map((m) => `${m.id}:${m.coordinates[0]}:${m.coordinates[1]}`).sort().join('|'), [validMarkers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || validMarkers.length === 0) return;
    const hasSignatureChanged = lastFitSignatureRef.current !== mapAssetFitSignature;
    if (hasFitBoundsRef.current && !hasSignatureChanged) return;
    if (fitMapToAssetBounds(map, validMarkers)) {
      hasFitBoundsRef.current = true;
      lastFitSignatureRef.current = mapAssetFitSignature;
    }
  }, [validMarkers, mapAssetFitSignature]);

  return (
    <div className="p-8">
      <div className="d-flex flex-wrap gap-2 mb-6 medium">
        <span className="badge bg-light text-dark p-8">Assets on map: {summary.total}</span>
        <span className="badge bg-light text-dark p-8">No location/invalid: {summary.noLocation}</span>
        <span className="badge bg-light text-dark p-8">Maintenance/down: {summary.maintenanceCount}</span>
        <span className="badge bg-light text-dark p-8">With open work: {summary.withWorkCount}</span>
      </div>
      {loading && <div className="alert alert-info py-2 mb-2">Loading map markers…</div>}
      {!loading && error && <div className="alert alert-danger py-2 mb-2">Unable to load map markers.</div>}
      {!loading && !error && validMarkers.length === 0 && <div className="alert alert-secondary py-2 mb-2">No assets with valid map coordinates for the current filters.</div>}
      {/* Marker clustering keeps map interaction responsive with dense asset datasets. */}
      <div style={{ height: 'calc(100vh - 250px)', width: '100%', position: 'relative' }}>
        <button type="button" className="btn btn-sm btn-light border position-absolute" style={{ top: 8, right: 8, zIndex: 1000 }} onClick={() => setLegendOpen((prev) => !prev)}>
          Legend
        </button>
        {legendOpen && <div className="card shadow-sm position-absolute p-8 ps-16 mt-8" style={{ top: 42, right: 8, zIndex: 1000, width: 220 }}>
          <div className="medium"><span className="me-8 text-lg" style={{ color: STATUS_RULES[6].color }}>●</span>Operational</div>
          <div className="medium"><span className="me-8 text-lg" style={{ color: STATUS_RULES[10].color }}>●</span>Maintenance</div>
          <div className="medium"><span className="me-8 text-lg" style={{ color: STATUS_RULES[7].color }}>●</span>Out of Service</div>
          <div className="medium"><span className="me-8 text-lg" style={{ color: STATUS_RULES.stale.color }}>●</span>Unknown</div>
        </div>}
        <MapContainer center={DEFAULT_MAP_CENTER} zoom={DEFAULT_MAP_ZOOM} style={{ height: '100%', width: '100%' }} ref={mapRef}>
          <TileLayer url={googleTileUrl} maxZoom={20} attribution='&copy; <a href="https://www.google.com/maps">Google Maps</a>' />
          <MarkerClusterGroup
            chunkedLoading
            iconCreateFunction={(cluster) => {
              const count = cluster.getChildCount();

              return L.divIcon({
                html: `
                    <div style="
                      width: 42px;
                      height: 42px;
                      border-radius: 50% 50% 50% 0;
                      background: #EC612C;
                      color: #fff;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      font-size: 15px;
                      font-weight: 700;
                      border: 3px solid #fff;
                      box-shadow: 0 2px 8px rgba(0,0,0,0.35);
                      transform: rotate(-45deg);
                    ">
                      <span style="transform: rotate(45deg);">
                        ${count}
                      </span>
                    </div>
                  `,
                className: '',
                iconSize: [42, 42],
                iconAnchor: [21, 42],
              });
            }}
          >
            {validMarkers.map((marker) => (
              <Marker
                key={marker.id}
                position={marker.coordinates}
                icon={buildMarkerIcon(marker.statusMeta.color, marker.assetNumber || 'Asset')}
              >
                <Popup minWidth={260}>
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-6">
                      <div className="fw-semibold">
                        {marker.assetNumber || 'Asset'}
                      </div>

                      <div className="d-flex align-items-center text-sm fw-semibold">
                        <span
                          className="me-6 "
                          style={{
                            display: 'inline-block',
                            position: 'relative',
                            top: '0px',
                            width: 11,
                            height: 11,
                            borderRadius: '50%',
                            backgroundColor: marker?.statusMeta?.color || STATUS_RULES.unknown.color,
                          }}
                        />
                        {marker.plantStatusName || marker.statusMeta.label}
                      </div>
                    </div>
                    {marker.assetDescription &&
                      <div>{marker.assetDescription}</div>}
                    {(marker.siteName || marker.projectName || marker.locationAddress) && <div className="mt-1">
                      {[marker.siteName, marker.projectName, marker.locationAddress].filter(Boolean).join(' / ')}
                    </div>}
                    {formatDateTime(marker.locationTimestamp) &&
                      <div className="text-muted text-xs mt-8">Updated: {formatDateTime(marker.locationTimestamp)}</div>}
                    {Number.isFinite(Number(marker.openWorkOrderCount)) &&
                      <div>Open WO: {Number(marker.openWorkOrderCount)}</div>}
                    {Number.isFinite(Number(marker.openWorkRequestCount ?? marker.openDefectCount)) && <
                      div>Open WR/Defects: {Number(marker.openWorkRequestCount ?? marker.openDefectCount)}</div>}
                    <div className="d-flex flex-wrap gap-2 mt-12">
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onOpenAsset?.(marker.assetId)}>Open Asset</button>
                      {/*<Link className="btn btn-sm btn-outline-secondary" to="/work-orders" state={{ prefillAssetId: marker.assetId, createOnLoad: true }}>Create WO</Link>*/}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
        </MapContainer>
      </div>
    </div>
  );
};

export default AssetMapView;
